// ForgeRock (AM), the provider in staging and prod.
//
// Sign-in runs through the Engage /login page, a route of this same bundle that
// drives AM's authentication journey (credentials, MFA, federation buttons) and
// then completes the OIDC authorize request. Sessions end on AM idle time or
// logout; tokens are introspected by the API.

import { AUTH_ROUTE_PATHS } from "../../authRoutePaths";

// ForgeRock's Engage client also gets a refresh token, and oidc-client renews
// with it in the background. That grant doesn't touch the AM session either,
// and AM's own idle limit (8h on QA) is far longer than ours, so this gate is
// the effective idle control: once the user has been idle past idleTimeoutMs,
// renewal is declined, the token expires and the session moves to
// SESSION_EXPIRED.
const isIdleBeyondTimeout = (idleTimeoutMs, lastActivity) => {
  // Missing means "no gate", not zero (which would decline every renewal).
  // appsettings.test.js pins a value onto every ForgeRock env.
  if (!idleTimeoutMs || typeof lastActivity !== "number") {
    return false;
  }
  return Date.now() - lastActivity >= idleTimeoutMs;
};

export const createForgeRockProvider = (settings) => ({
  id: "forgerock",
  settings,

  // --- Discovery and client registration ---------------------------------

  wellKnownUrl: settings.wellKnownUrl,
  scope: settings.scope,

  // The browser and the app's WebView are separate AM clients. Also the suffix
  // of the key oidc-client stores the user under (oidc.user:<issuer>:<client>).
  clientId: () =>
    globalThis.ReactNativeWebView
      ? settings.clientIds?.appWebView
      : settings.clientIds?.browser,

  // --- UserManager ---------------------------------------------------------

  // Authorize through the Engage /login page rather than AM's own endpoint.
  authorizationEndpoint: settings.loginPageUrl,
  responseMode: "query",

  // --- Tokens --------------------------------------------------------------

  // The API introspects the access token with AM.
  apiBearer: "access_token",

  // An expired ForgeRock token is an expired session: the idle gate below is
  // what bounds a session, and renewing past it would undo that.
  renewsExpiredSessions: false,

  // Background renewal stops once the user goes idle (see isIdleBeyondTimeout).
  tracksUserActivity: true,
  shouldRenewInBackground: (lastActivity) =>
    !isIdleBeyondTimeout(settings.idleTimeoutMs, lastActivity),

  // AM keeps honouring a refresh token after the access token (and even the AM
  // session) is gone, so logout revokes both.
  revokesTokensOnLogout: true,

  // --- Sign-in -------------------------------------------------------------

  // The authorize request opens a route of this bundle, so a sign-in can start
  // without a page load (see LoginService).
  hasInAppLoginPage: true,
  loginPageUrl: settings.loginPageUrl,

  // The /login page shows the "session expired" banner from this flag.
  signinParams: ({ isSessionExpired }) => ({
    ...(isSessionExpired && { sessionExpired: "1" }),
  }),

  // Nothing to reset: the /login page decides what to show on its own.
  abandonSignin: () => {},

  // Account expired: the /login page shows the reason.
  blockedPath: AUTH_ROUTE_PATHS.LOGIN,

  // A failed callback restarts sign-in on the /login form, which waits for the
  // user, so restarts can't loop.
  shouldStopRestartingSignin: () => false,
  onSigninCompleted: () => {},
  onProviderRefusal: () => {},

  // --- Sign-out ------------------------------------------------------------

  // Nothing to arm: AM ends its own session on end-session.
  beforeSignout: () => {},
  // id_token_hint (sent for both providers) is all AM needs.
  signoutParams: () => ({}),

  // --- Routes ----------------------------------------------------------------

  // PageLogin is both the /login page and the callback: AM returns the
  // authorization response in the query string, which it reads.
  loadLoginPage: () => import("@/screens/login"),
  loadCallbackPage: () => import("@/screens/login"),
});
